import { useCallback, useEffect, useState } from 'react'
import { Form } from 'antd'
import { routes } from 'router'
import { VALIDATION_MESSAGES } from 'constants/txt'
import { notification } from 'components/Notification'
import { useVendorDetailsContext } from '../../../Providers/ContextProvider'
import { convertDetailsToForm, convertFormToCreationDetails } from '../utils'
import useRouter from 'hooks/useRouter'
import { ability, ACTIONS, SUBJECTS } from 'features/Permission'
import { MY_COMMUNITY_KEYS } from '../../../../../constants'
import { VendorDetailsForm } from '../types'

const useDetailsForm = () => {
  const router = useRouter()

  const vendorDetailsContext = useVendorDetailsContext()

  const { details, isCreation } = vendorDetailsContext.state
  const { createVendorWithAdminAsync, setErrors } = vendorDetailsContext.actions

  const [form] = Form.useForm<VendorDetailsForm>()

  const [hasFormChanges, _setHasFormChanges] = useState(false)
  const [isActive, setIsActive] = useState(false)
  const [showRedirectModal, setShowRedirectModal] = useState(false)
  const [createdId, setCreatedId] = useState<string>('')

  const canCreateOnlyGuestAccess = ability.can(
    ACTIONS.CREATE,
    SUBJECTS.ONLY_GUEST_VENDORS
  )

  const triggerHasFormChanges = useCallback(() => {
    if (!hasFormChanges) {
      _setHasFormChanges(true)
    }
  }, [hasFormChanges])

  const handleFinishForm = useCallback(
    async (values: VendorDetailsForm) => {
      const creationDetails = convertFormToCreationDetails(values)

      setIsActive(false)
      const vendorUuid = await createVendorWithAdminAsync(creationDetails)

      if (vendorUuid) {
        if (values?.vendor.vendor_permission !== 'guest') {
          notification.success({
            message: VALIDATION_MESSAGES.SM0055
          })
        } else {
          notification.success({
            message: VALIDATION_MESSAGES.SM0055_1
          })
        }
        setShowRedirectModal(true)
        setCreatedId(vendorUuid)
        _setHasFormChanges(false)
      }
    },
    [createVendorWithAdminAsync]
  )

  const handleRedirectModal = (withUsers: boolean) => {
    if (withUsers) {
      router.push(
        `${routes.myCommunity}/${MY_COMMUNITY_KEYS.vendor_details}/${createdId}/vendor_users`
      )
    } else {
      router.push(
        `${routes.myCommunity}/${MY_COMMUNITY_KEYS.vendor_details}/${createdId}/general`
      )
    }
  }

  const handleCancelForm = useCallback(() => {
    form.setFieldsValue(convertDetailsToForm(details, canCreateOnlyGuestAccess))
    router.push(routes.myCommunity + '/' + MY_COMMUNITY_KEYS.vendors)
    _setHasFormChanges(false)
  }, [details, form, router, canCreateOnlyGuestAccess])

  const handleChangeForm = useCallback(() => {
    setErrors({})
    triggerHasFormChanges()
  }, [setErrors, triggerHasFormChanges])

  useEffect(() => {
    form.setFieldsValue(convertDetailsToForm(details, canCreateOnlyGuestAccess))
  }, [details, form, canCreateOnlyGuestAccess])

  return {
    form,
    isCreation,
    hasFormChanges,
    handleFinishForm,
    handleCancelForm,
    handleChangeForm,
    details,
    isActive,
    setIsActive,
    handleRedirectModal,
    showRedirectModal
  }
}

export default useDetailsForm
